// Setpoint flags — user-defined threshold conditions on sensor readings.
//
// Persisted to localStorage. Will sync to backend once the AlertsEngine API
// is in place (setpoints are a degenerate one-trigger alert and the engine
// can hydrate them as `Setpoint`-typed rules without graph nodes).

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Setpoint, SetpointOp } from '@/types';

interface SetpointStore {
  setpoints: Record<string, Setpoint>;
  upsert:    (sp: Setpoint) => void;
  remove:    (id: string) => void;
  toggle:    (id: string) => void;
}

export const useSetpointStore = create<SetpointStore>()(
  persist(
    (set, get) => ({
      setpoints: {},
      upsert: (sp) => set((s) => ({ setpoints: { ...s.setpoints, [sp.id]: sp } })),
      remove: (id) => set((s) => {
        const { [id]: _, ...rest } = s.setpoints;
        return { setpoints: rest };
      }),
      toggle: (id) => {
        const sp = get().setpoints[id];
        if (!sp) return;
        set((s) => ({ setpoints: { ...s.setpoints, [id]: { ...sp, enabled: !sp.enabled } } }));
      },
    }),
    { name: 'lc-setpoints-v1', version: 1 },
  ),
);

export function newSetpointId(): string {
  return `sp_${Math.random().toString(36).slice(2, 10)}`;
}

export function evalSetpoint(value: number, op: SetpointOp, threshold: number): boolean {
  switch (op) {
    case '>':  return value >  threshold;
    case '<':  return value <  threshold;
    case '>=': return value >= threshold;
    case '<=': return value <= threshold;
    case '=':  return value === threshold;
    case '!=': return value !== threshold;
  }
}

/** Find the *first* setpoint that fires for a given (machine, field, value).
 *  Returns null when:
 *    - value is not a finite number (NaN / undefined / string)
 *    - no enabled setpoint matches the (sensor, scope) tuple
 *    - the threshold itself is non-finite (defensive: blank input in
 *      the setpoints sheet sometimes left `value: NaN` which compared
 *      truthy for `>` on every real reading) */
export function findFiringSetpoint(
  setpoints: Record<string, Setpoint>,
  machineKey: string,
  field: string,
  value: number | null | undefined,
): Setpoint | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  for (const sp of Object.values(setpoints)) {
    if (!sp.enabled) continue;
    if (sp.sensor !== field) continue;
    if (!Number.isFinite(sp.value)) continue;
    if (sp.scope === 'machines' && (!sp.machineKeys || !sp.machineKeys.includes(machineKey))) continue;
    if (evalSetpoint(value, sp.op, sp.value)) {
      if ((import.meta as any).env?.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[setpoint] fires', {
          id: sp.id, machineKey, field, value, op: sp.op, threshold: sp.value, scope: sp.scope,
        });
      }
      return sp;
    }
  }
  return null;
}

/** All setpoints applicable to (machine, field). Used to render chart threshold lines. */
export function applicableSetpoints(
  setpoints: Record<string, Setpoint>,
  machineKey: string,
  field: string,
): Setpoint[] {
  return Object.values(setpoints).filter((sp) =>
    sp.enabled &&
    sp.sensor === field &&
    Number.isFinite(sp.value) &&
    (sp.scope === 'global' || (sp.machineKeys?.includes(machineKey) ?? false)),
  );
}

export function useSetpoints(): Record<string, Setpoint> {
  return useSetpointStore((s) => s.setpoints);
}
